import React, { useEffect, useRef, useState } from 'react';
import { Eye, Target, Sparkles } from 'lucide-react';
import { visionContent, missionContent } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings, pickCmsText, makeSectionCopyPicker } from '../services/publicContentService';

const VisionMission = ({ theme }) => {
  const videoRef = useRef(null);
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [cms, setCms] = useState({}); // { vision:{ar,en}, mission:{ar,en} }
  const [sc, setSc] = useState({});

  useEffect(() => {
    let alive = true;
    fetchPublicSettings('company_info').then((v) => {
      if (alive) setCms(v || {});
    });
    fetchPublicSettings('section_copy').then((v) => { if (alive) setSc(v || {}); });
  }, []);
  const sec = makeSectionCopyPicker(sc, isAr);

  // Ensure video plays when component mounts or theme changes
  useEffect(() => {
    const playVideo = async () => {
      if (videoRef.current) {
        try {
          videoRef.current.load();
          await videoRef.current.play();
        } catch (error) {
          setTimeout(() => {
            if (videoRef.current) videoRef.current.play().catch(() => {});
          }, 500);
        }
      }
    };
    playVideo();
  }, [theme]);

  // Only accept CMS text when it looks like a real paragraph, so leftover
  // short test strings don't replace the rich static fallback.
  const cmsVisionAr  = pickCmsText(cms.vision,  true,  60);
  const cmsVisionEn  = pickCmsText(cms.vision,  false, 60);
  const cmsMissionAr = pickCmsText(cms.mission, true,  60);
  const cmsMissionEn = pickCmsText(cms.mission, false, 60);

  const visionAr  = cmsVisionAr  || visionContent.content;
  const visionEn  = cmsVisionEn  || visionContent.contentEn;
  const missionAr = cmsMissionAr || missionContent.content;
  const missionEn = cmsMissionEn || missionContent.contentEn;

  const cards = [
    {
      id: 'vision',
      title: sec('vision_title', isAr ? visionContent.title : visionContent.titleEn),
      content: isAr ? visionAr : visionEn,
      Icon: Eye,
      gradient: 'from-cyan-400 via-sky-400 to-blue-500',
      glowColor: 'rgba(34, 211, 238, 0.45)',
      eyebrow: 'V/01',
    },
    {
      id: 'mission',
      title: sec('mission_title', isAr ? missionContent.title : missionContent.titleEn),
      content: isAr ? missionAr : missionEn,
      Icon: Target,
      gradient: 'from-emerald-400 via-teal-400 to-cyan-500',
      glowColor: 'rgba(16, 185, 129, 0.40)',
      eyebrow: 'M/02',
    },
  ];

  return (
    <section id="vision-mission" dir={isAr ? 'rtl' : 'ltr'} className="relative py-32 overflow-hidden">
      {/* Underwater video background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src={
              theme === 'light'
                ? 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/spjb1ya7_13058544_1080_1920_60fps.mp4'
                : 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/ds0x3i5q_14108098_1080_1920_60fps.mp4'
            }
            type="video/mp4"
          />
        </video>

        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-blue-950/55 to-slate-950/70 dark:from-slate-950/75 dark:via-blue-950/65 dark:to-slate-950/80" />
      </div>

      {/* Light rays from top */}
      <div className="absolute inset-0 overflow-hidden opacity-25 pointer-events-none">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="light-ray"
            style={{
              left: `${15 + i * 17}%`,
              animationDelay: `${i * 1.5}s`,
              animationDuration: '14s',
            }}
          />
        ))}
      </div>

      {/* Minimal floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-cyan-300/25"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              animation: `float-up ${7 + Math.random() * 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative container mx-auto px-4">
        {/* Section eyebrow */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-400/30 backdrop-blur-md mb-5">
            <Sparkles className="w-4 h-4 text-cyan-300" />
            <span className="text-xs tracking-[0.3em] uppercase text-cyan-100">
              {sec('visionmission_eyebrow', isAr ? 'توجهنا الاستراتيجي' : 'Our Strategic Direction')}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight" data-testid="visionmission-heading">
            {isAr ? (
              <>{sec('visionmission_heading_left', 'رؤيتنا')} <span className="text-cyan-300/60 mx-2">·</span> {sec('visionmission_heading_right', 'رسالتنا')}</>
            ) : (
              <>{sec('visionmission_heading_left', 'Vision')} <span className="text-cyan-300/60 mx-2">·</span> {sec('visionmission_heading_right', 'Mission')}</>
            )}
          </h2>
        </div>

        {/* Two floating glass cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 max-w-7xl mx-auto">
          {cards.map(({ id, title, content, Icon, gradient, glowColor, eyebrow }, index) => (
            <article
              key={id}
              data-testid={`vision-mission-card-${id}`}
              className="group relative animate-fade-in-up"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              {/* Soft glow halo behind card — intensifies on hover */}
              <div
                className="absolute -inset-2 rounded-[2rem] blur-2xl opacity-50 group-hover:opacity-90 transition-opacity duration-700 pointer-events-none"
                style={{ background: glowColor }}
              />

              {/* Subtle ripple ring on hover */}
              <div className="absolute inset-0 rounded-[2rem] pointer-events-none">
                <div className="absolute inset-0 rounded-[2rem] border border-cyan-300/0 group-hover:border-cyan-300/40 transition-colors duration-700" />
              </div>

              {/* Glass Card */}
              <div
                className="relative h-full rounded-[2rem] p-8 md:p-10 lg:p-12 overflow-hidden
                           bg-white/[0.07] dark:bg-white/[0.04]
                           backdrop-blur-2xl
                           border border-white/15
                           shadow-[0_20px_60px_-15px_rgba(8,145,178,0.4)]
                           transition-all duration-700
                           group-hover:-translate-y-2
                           group-hover:bg-white/[0.10] group-hover:border-white/25
                           group-hover:shadow-[0_30px_80px_-15px_rgba(8,145,178,0.6)]"
              >
                {/* Top gradient hairline */}
                <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${gradient} opacity-80`} />

                {/* Faint corner accents */}
                <div className="absolute top-4 right-4 w-10 h-10 border-t border-r border-cyan-300/30 rounded-tr-2xl pointer-events-none" />
                <div className="absolute bottom-4 left-4 w-10 h-10 border-b border-l border-cyan-300/30 rounded-bl-2xl pointer-events-none" />

                {/* Internal soft glow */}
                <div
                  className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl pointer-events-none transition-opacity duration-700 opacity-50 group-hover:opacity-80"
                  style={{ background: glowColor }}
                />

                <div className={`relative ${isAr ? 'text-right' : 'text-left'}`}>
                  {/* Eyebrow + Icon row */}
                  <div className={`flex items-center justify-between mb-8 ${isAr ? 'flex-row-reverse' : ''}`}>
                    {/* Floating glowing icon */}
                    <div
                      className="relative animate-float-slow"
                      style={{ animationDelay: `${index * 0.4}s` }}
                    >
                      {/* Icon glow halo */}
                      <div
                        className="absolute inset-0 rounded-2xl blur-xl opacity-70"
                        style={{ background: glowColor }}
                      />
                      <div
                        className={`relative w-16 h-16 md:w-18 md:h-18 rounded-2xl bg-gradient-to-br ${gradient}
                                    flex items-center justify-center
                                    shadow-lg shadow-cyan-500/40
                                    border border-white/30
                                    transition-transform duration-500
                                    group-hover:scale-110 group-hover:rotate-3`}
                      >
                        <Icon className="w-8 h-8 md:w-9 md:h-9 text-white drop-shadow-md" strokeWidth={1.8} />
                      </div>
                    </div>

                    {/* Eyebrow code */}
                    <span className="text-xs tracking-[0.3em] font-mono uppercase text-cyan-200/60">
                      {eyebrow}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight leading-tight">
                    {title}
                  </h3>

                  {/* Decorative underline */}
                  <div
                    className={`h-0.5 w-16 mb-6 ${isAr ? 'mr-0 ml-auto bg-gradient-to-l' : 'ml-0 mr-auto bg-gradient-to-r'} ${gradient} rounded-full
                                transition-all duration-500 group-hover:w-28`}
                  />

                  {/* Content — always visible */}
                  <p
                    className="text-lg md:text-xl leading-loose text-slate-100/90"
                    style={{ direction: isAr ? 'rtl' : 'ltr' }}
                  >
                    {content}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default VisionMission;
